//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Home as CarbonHome,
  Analytics,
  Plug,
  Book,
  Settings as CarbonSettings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Add,
} from '@carbon/icons-react';
import { AlertTriangle, CheckCircle, X } from 'lucide-react';
import { stringToColor } from '../../utils/colorUtils';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { getInitials } from '../../utils/avatarUtils';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../UserAuth';
import { useTeamspaceContext } from '../../TeamspaceContext';
import TenantDataService from '../../utils/TenantDataService';
import storageService from '../../utils/storageService';

import { InfinitySymbol } from '@carbon/icons-react';
import {
  TeamspaceIdentifier,
  FetchTeamspaceUserResponse,
} from './SidebarTypes';
import EditModal from '../Loops/Edit/EditModal';
import { useConfig } from '../../Config';
import { setTeamspacesList } from '../Loops/response';
import { cn } from '../../components/ui/utils';
import {
  fetchWikiStatus,
  getCachedWikiStatus,
} from '../../utils/wikiStatusCache';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import { useLoopContext } from '../../LoopContext';
import LoopSwitcher from './LoopSwitcher';

interface Services {
  license: { url: string };
  loop: { url: string; show: boolean };
  tenant: { url: string; show: boolean };
  edit: {
    manage_users_teamspace: { show: boolean };
    manage_users_loop: { show: boolean };
  };
}

interface SidebarProps {
  userTeamspaces: TeamspaceIdentifier[];
  topOffset?: number;
}

// Alert color constants used in inline styles (cannot be expressed as Tailwind classes)
const ALERT_COLORS = {
  error: 'hsl(var(--destructive))',
  success: '#2e7d32',
} as const;

// --- Sidebar styled sub-components ---

interface NavItemButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive: boolean;
  centered?: boolean;
}

const NavItemButton = React.forwardRef<HTMLButtonElement, NavItemButtonProps>(
  ({ isActive, centered, className, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'mx-1 mb-1 flex w-[calc(100%-8px)] items-center gap-3 rounded p-2 text-left transition-colors',
        centered && 'justify-center',
        isActive
          ? 'bg-brand-nav text-white'
          : 'text-gray-200 hover:bg-gray-800',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  ),
);
NavItemButton.displayName = 'NavItemButton';

interface NavItemLinkProps extends React.ComponentProps<typeof Link> {
  isActive: boolean;
  centered?: boolean;
}

const NavItemLink = ({
  isActive,
  centered,
  className,
  children,
  ...props
}: NavItemLinkProps) => (
  <Link
    className={cn(
      'mx-1 mb-1 flex w-[calc(100%-8px)] items-center gap-3 rounded p-2 transition-colors',
      centered && 'justify-center',
      isActive ? 'bg-brand-nav text-white' : 'text-gray-200 hover:bg-gray-800',
      className,
    )}
    {...props}
  >
    {children}
  </Link>
);

interface TeamspaceAvatarProps {
  name: string | undefined;
  className?: string;
  color?: string;
  fallbackProps?: React.HTMLAttributes<HTMLSpanElement> & {
    'data-testid'?: string;
    id?: string;
  };
}

const TeamspaceAvatar = ({
  name,
  className,
  color,
  fallbackProps,
}: TeamspaceAvatarProps) => (
  <Avatar className={cn('size-8', className)}>
    <AvatarFallback
      style={{ backgroundColor: color ?? stringToColor(name || 'T') }}
      {...fallbackProps}
    >
      {getInitials(name, 'T')}
    </AvatarFallback>
  </Avatar>
);

const SidebarSubLink = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Link>) => (
  <Link
    className={cn(
      'block rounded p-2 text-sm text-white transition-colors hover:bg-[#4A4A56]',
      className,
    )}
    {...props}
  >
    {children}
  </Link>
);

const DarkMenuItem = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuItem>) => (
  <DropdownMenuItem
    className={cn(
      '!gap-2 !rounded-none !px-4 !py-2 !text-sm !text-gray-300 data-[highlighted]:!bg-[#4A4A56]',
      className,
    )}
    {...props}
  >
    {children}
  </DropdownMenuItem>
);

const Sidebar: React.FC<SidebarProps> = ({ userTeamspaces, topOffset = 0 }) => {
  // Filter out 'Initial TeamSpace' from the teamspaces list
  const filteredTeamspaces = userTeamspaces.filter(
    (ts) => ts.name !== 'Initial TeamSpace',
  );
  const { t } = useTranslation();
  const { services } = useConfig() as { services: Services | undefined };
  const navigate = useNavigate();
  const location = useLocation();
  const { user, roles, isAuthenticated, licenses } = useUserAuth();
  const {
    teamspaceName,
    teamspaceId,
    _setTeamspaceName,
    _setTeamspaceId,
    _setTeamspaceSlug,
    teamSpaceCreationFailed,
    _setCreateNewTeamSpace,
    _setTeamspaceUsers,
    _setIncompleteTeamspaceTask,
    _setTeamspaceList,
  } = useTeamspaceContext();
  const {
    activeLoopId,
    activeLoopName,
    isLoopContextActive,
    setLoopContext,
    clearLoopContext,
  } = useActiveLoopContext();
  const { completedLoops, setSelectedLoop } = useLoopContext();
  const [teamspaceMenuOpen, setTeamspaceMenuOpen] = useState(false);
  const teamspaceCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [open, setOpen] = useState(() => {
    const sidebarOpen = storageService.getLocalItemWithKey(
      storageService.KEYS.SIDEBAR_EXPANDED,
    );
    return sidebarOpen === 'true';
  });
  const [selectedSidebarIndex, setSelectedSidebarIndex] = useState(0);
  const teamspaceColors: Record<string, string> = Object.fromEntries(
    filteredTeamspaces
      .filter((ts) => ts.id && ts.iconColor)
      .map((ts) => [ts.id!, ts.iconColor!]),
  );

  // Listen for NavButtons event to select Home
  useEffect(() => {
    const handleSelectSidebarHome = () => {
      setSelectedSidebarIndex(0); // Home is always index 0
    };
    window.addEventListener('selectSidebarHome', handleSelectSidebarHome);
    return () => {
      window.removeEventListener('selectSidebarHome', handleSelectSidebarHome);
    };
  }, []);

  const [selectedTeamspaceIndex, setSelectedTeamspaceIndex] = useState(0);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [associatedUsers, setAssociatedUsers] = useState<
    { username: string; fullname: string }[]
  >([]);
  const hasAlreadyFetched = useRef<boolean>(false);
  const tenantDataService = new TenantDataService(
    () => {
      return user;
    },
    () => {
      return isAuthenticated;
    },
  );
  const [isUserAdminDropdownOpen, setIsUserAdminDropdownOpen] = useState(false);
  const [isDropdownModuleVisible, setIsDropdownModuleVisible] = useState(false);
  const [settingsPopoverOpen, setSettingsPopoverOpen] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const currentTeamspaceRole = useMemo(() => {
    if (teamspaceId) {
      const currentTeamspace = filteredTeamspaces.find(
        (teamspace) => teamspace?.id === teamspaceId,
      );
      return currentTeamspace?.role;
    }
    return '';
  }, [teamspaceId, filteredTeamspaces]);

  const handleCloseSnackbar = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const validTeamspaces = filteredTeamspaces
      .filter((ts) => ts.id && ts.name)
      .map((ts) => ({
        ...ts,
        id: ts.id as string,
        name: ts.name as string,
      }));
    setTeamspacesList(validTeamspaces, _setTeamspaceList);
  }, [userTeamspaces]);

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning' = 'success',
  ) => {
    setSnackbarState({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    if (snackbarState.open) {
      const timer = setTimeout(() => {
        setSnackbarState((prev) => ({ ...prev, open: false }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [snackbarState.open]);

  const getAlertIcon = (severity: 'success' | 'error' | 'info' | 'warning') => {
    switch (severity) {
      case 'error':
        return <AlertTriangle className="size-5 text-destructive" />;
      case 'success':
      default:
        return <CheckCircle className="size-5 text-[#2e7d32]" />;
    }
  };

  const getBorderColor = (
    severity: 'success' | 'error' | 'info' | 'warning',
  ) => {
    const color =
      severity === 'error' ? ALERT_COLORS.error : ALERT_COLORS.success;
    return `4px solid ${color}`;
  };

  const showSnackbarForEditModal = (message: string, type?: string) => {
    showSnackbar(message, type as 'success' | 'error' | 'info' | 'warning');
  };

  const fetchUsersOfTeamspace = async (immediateTeamspaceId?: string) => {
    try {
      const users: FetchTeamspaceUserResponse =
        await tenantDataService.fetchTeamspaceMembers(
          immediateTeamspaceId || teamspaceId || '',
        );

      setAssociatedUsers(
        users.map((user) => ({
          username: user.username,
          fullname: user.fullname || user.username,
        })),
      );
      // setting context for teamSpaceUsers
      const teamSpaceUsers = users.map((user: any) => ({
        id: user.id || user.keycloakId || '',
        username: user.username,
        fullname: user.fullname || '',
        keycloakId: user.keycloakId,
        firstname: user.firstname,
        lastname: user.lastname,
      }));

      _setTeamspaceUsers(teamSpaceUsers);
    } catch (error) {}
  };

  const handleEditTeamspace = async () => {
    if (!hasAlreadyFetched.current) {
      await fetchUsersOfTeamspace();
      hasAlreadyFetched.current = true;
    }
    setEditModalVisible(true);
    setTeamspaceMenuOpen(false);
  };

  const handleCreateTeamspace = () => {
    _setCreateNewTeamSpace(true);
    navigate('/teamspace');
    setTeamspaceMenuOpen(false);
  };

  const handleCloseEditTeamspace = () => {
    setEditModalVisible(false);
    hasAlreadyFetched.current = false;
  };
  const storeSidebarState = (sidebarExpanded: boolean) => {
    // Set sidebar expanded value in local storage
    storageService.setLocalItemWithKey(
      storageService.KEYS.SIDEBAR_EXPANDED,
      sidebarExpanded.toString(),
    );
  };

  const handleSidebarItemClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    index: number,
    itemName?: string,
  ) => {
    if (itemName !== t('Settings')) {
      setSelectedSidebarIndex(index);
    }
    if (itemName === t('user-administration')) {
      if (!open) {
        // Toggle the dropdown module when the sidebar is collapsed
        setIsDropdownModuleVisible(!isDropdownModuleVisible);
      } else {
        // Existing behavior for expanded sidebar
        setIsUserAdminDropdownOpen(!isUserAdminDropdownOpen);
      }
    }
  };

  const handleDrawerOpen = () => {
    storeSidebarState(true);
    setOpen(true);
  };

  const handleDrawerClose = () => {
    storeSidebarState(false);
    setOpen(false);
  };

  const handleTeamspaceMenuItemClick = (index: number) => {
    clearLoopContext();
    setSelectedTeamspaceIndex(index);
    _setTeamspaceName(filteredTeamspaces[index]?.name);
    _setTeamspaceId(filteredTeamspaces[index]?.id);
    _setTeamspaceSlug(filteredTeamspaces[index]?.slug);
    storageService.setCurrentTeamspace(
      filteredTeamspaces[index]?.id,
      filteredTeamspaces[index]?.name,
    );
    fetchUsersOfTeamspace(filteredTeamspaces[index]?.id);
    hasAlreadyFetched.current = true;
    setTeamspaceMenuOpen(false);
    if (filteredTeamspaces[index]?.incompleteTask) {
      // If teamspace has an incomplete task, then set it in context
      _setIncompleteTeamspaceTask(
        filteredTeamspaces[index]?.incompleteTask ?? '',
      );
    }
    // Always navigate to home route with teamspace data
    navigate('/', {
      state: {
        teamspaceId: filteredTeamspaces[index]?.id,
        teamspaceName: filteredTeamspaces[index]?.name,
      },
    });
    // If teamspace creation failed or incomplete, still allow navigation to teamspace page
    if (
      filteredTeamspaces[index]?.failedTask ||
      filteredTeamspaces[index]?.incompleteTask
    ) {
      navigate('/teamspace');
    }
  };

  const handleTeamspaceMenuClose = () => {
    setTeamspaceMenuOpen(false);
  };

  const handleTeamspaceMouseEnter = () => {
    if (teamspaceCloseTimeoutRef.current) {
      clearTimeout(teamspaceCloseTimeoutRef.current);
      teamspaceCloseTimeoutRef.current = null;
    }
  };

  const handleTeamspaceMouseLeave = () => {
    teamspaceCloseTimeoutRef.current = setTimeout(() => {
      setTeamspaceMenuOpen(false);
    }, 300);
  };

  // Sidebar open state is initialized from localStorage in useState above

  const isAdmin = useMemo(() => roles.includes('ROLE_ADMIN'), [roles]);
  const isTenantShow = services?.tenant?.show ?? false;
  const isSettingsRouteActive = useMemo(
    () =>
      [
        '/user-administration',
        '/integration',
        '/announcements',
        '/user-groups',
      ].some((route) => location.pathname.startsWith(route)),
    [location.pathname],
  );

  // Derive the effective sidebar index: -1 when on settings routes, otherwise the selected index
  const effectiveSidebarIndex = isSettingsRouteActive
    ? -1
    : selectedSidebarIndex;

  useEffect(() => {
    if (isAuthenticated && user.access_token && teamspaceId) {
      void (async () => {
        await fetchUsersOfTeamspace(teamspaceId);
        hasAlreadyFetched.current = true;
      })();
    }
  }, [isAuthenticated, user.access_token, teamspaceId]);

  // Wiki nav item visibility is checked lazily for only the active teamspace
  // (not the entire teamspace list) and cached, to avoid a Control lookup
  // per teamspace on every teamspace-list refresh.
  const [wikiFetchResult, setWikiFetchResult] = useState<
    { teamspaceId: string; hasWiki: boolean } | undefined
  >(undefined);
  const cachedTeamspaceHasWiki = teamspaceId
    ? getCachedWikiStatus(teamspaceId)
    : undefined;
  // Derived during render; the effect below only setState()s from its async callback.
  const currentTeamspaceHasWiki =
    cachedTeamspaceHasWiki ??
    (wikiFetchResult && wikiFetchResult.teamspaceId === teamspaceId
      ? wikiFetchResult.hasWiki
      : undefined);

  useEffect(() => {
    if (!teamspaceId || getCachedWikiStatus(teamspaceId) !== undefined) {
      return;
    }
    let cancelled = false;
    fetchWikiStatus(teamspaceId, () =>
      tenantDataService.getTeamspaceWikiStatus(teamspaceId),
    ).then((result) => {
      if (!cancelled) {
        setWikiFetchResult({ teamspaceId, hasWiki: result });
      }
    });
    return () => {
      cancelled = true;
    };
    // tenantDataService is recreated each render; only re-fetch on teamspace change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamspaceId]);

  interface SidebarItem {
    name: string;
    route: string;
    icon: JSX.Element;
    subItems?: { name: string; route: string }[];
  }

  const getLoopSidebarItems = (): SidebarItem[] => {
    const items: SidebarItem[] = [
      {
        name: t('loop-details'),
        route: '/loop-details',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={'id-sidebar-loop-details-icon-button'}
                id={'id-sidebar-loop-details-icon-button'}
              >
                <InfinitySymbol
                  size={20}
                  id="id-sidebar-loop-details-icon"
                  data-testid="id-sidebar-loop-details-icon"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{t('loop-details')}</TooltipContent>
          </Tooltip>
        ),
      },
      {
        name: t('dashboards'),
        route: '/dashboard',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={'id-sidebar-dashboards-icon'}
                id={'id-sidebar-dashboards-icon'}
              >
                <Analytics size={20} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{t('dashboards')}</TooltipContent>
          </Tooltip>
        ),
      },
    ];

    return items;
  };

  const getTeamspaceSidebarItems = (): SidebarItem[] => {
    const items: SidebarItem[] = [
      {
        name: t('Home'),
        route: '/',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={'id-sidebar-home-icon-button'}
                id={'id-sidebar-home-icon-button'}
              >
                <CarbonHome
                  size={20}
                  id="id-sidebar-home-icon"
                  data-testid="id-sidebar-home-icon"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{t('Home')}</TooltipContent>
          </Tooltip>
        ),
      },
      {
        name: t('dashboards'),
        route: '/dashboard',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={'id-sidebar-dashboards-icon'}
                id={'id-sidebar-dashboards-icon'}
              >
                <Analytics size={20} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{t('dashboards')}</TooltipContent>
          </Tooltip>
        ),
      },
      {
        name: t('plugins'),
        route: '/plugin',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={'id-sidebar-plugins-icon'}
                id={'id-sidebar-plugins-icon'}
              >
                <Plug size={20} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{t('plugins')}</TooltipContent>
          </Tooltip>
        ),
      },
    ];

    if (currentTeamspaceHasWiki) {
      items.push({
        name: t('wiki.tabLabel'),
        route: '/wiki',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={'sidebar-wiki-nav-item'}
                id={'sidebar-wiki-nav-item'}
                aria-label={t('wiki.tabLabel')}
              >
                <Book size={20} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{t('wiki.tabLabel')}</TooltipContent>
          </Tooltip>
        ),
      });
    }

    if (isAdmin) {
      items.push({
        name: t('Settings'),
        route: '',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={'id-sidebar-settings-icon-button'}
                id={'id-sidebar-settings-icon-button'}
                aria-label={t('Settings')}
              >
                <CarbonSettings size={20} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{t('Settings')}</TooltipContent>
          </Tooltip>
        ),
        subItems: [
          {
            name: t('announcements'),
            route: '/announcements',
          },
          {
            name: t('user-administration'),
            route: '/user-administration',
          },
          {
            name: t('integration'),
            route: '/integration',
          },
          {
            name: t('userGroups.title'),
            route: '/user-groups',
          },
        ],
      });
    }

    return items;
  };

  const getSidebarItems = (): SidebarItem[] => {
    return isLoopContextActive
      ? getLoopSidebarItems()
      : getTeamspaceSidebarItems();
  };

  const buildSidebarItems = () => {
    return (
      <nav className="flex-1 overflow-y-auto px-0.5 py-1">
        {getSidebarItems().map((item, index) => {
          const isActive =
            item.name === t('Settings')
              ? isSettingsRouteActive
              : index === effectiveSidebarIndex;
          return (
            <React.Fragment key={item.name}>
              {item.name === t('Settings') ? (
                open ? (
                  <>
                    <NavItemButton
                      isActive={isActive}
                      onClick={() =>
                        setIsUserAdminDropdownOpen(!isUserAdminDropdownOpen)
                      }
                      aria-expanded={isUserAdminDropdownOpen}
                      aria-controls="sidebar-settings-subitems"
                      aria-label={
                        isUserAdminDropdownOpen
                          ? t('collapse-settings')
                          : t('expand-settings')
                      }
                    >
                      <span className="shrink-0">{item.icon}</span>
                      <span className="flex-1 text-sm">{item.name}</span>
                      <span
                        className={cn(
                          'shrink-0 transition-transform duration-300',
                          isUserAdminDropdownOpen && 'rotate-180',
                        )}
                      >
                        <ChevronDown size={12} />
                      </span>
                    </NavItemButton>
                    {isUserAdminDropdownOpen && (
                      <div
                        id="sidebar-settings-subitems"
                        className="mx-1 mb-0.5 rounded bg-[#33333D] pl-8"
                      >
                        {item.subItems?.map((subItem) => (
                          <SidebarSubLink
                            key={subItem.name}
                            to={subItem.route}
                            onClick={() => setIsUserAdminDropdownOpen(false)}
                          >
                            {subItem.name}
                          </SidebarSubLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Popover
                    open={settingsPopoverOpen}
                    onOpenChange={setSettingsPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <NavItemButton
                        isActive={isActive}
                        centered
                        onClick={(event) =>
                          handleSidebarItemClick(event, index, item.name)
                        }
                        aria-expanded={settingsPopoverOpen}
                        aria-controls="sidebar-settings-popover"
                        aria-label={
                          settingsPopoverOpen
                            ? t('collapse-settings')
                            : t('expand-settings')
                        }
                      >
                        {item.icon}
                      </NavItemButton>
                    </PopoverTrigger>
                    <PopoverContent
                      side="right"
                      align="start"
                      sideOffset={4}
                      className="!w-auto !min-w-[160px] !border-0 !bg-[#262626] !p-1"
                      id="id-sidebar-settings-popover"
                      data-testid="id-sidebar-settings-popover"
                    >
                      {item.subItems?.map((subItem) => (
                        <Tooltip key={subItem.name}>
                          <TooltipTrigger asChild>
                            <SidebarSubLink
                              to={subItem.route}
                              onClick={() => setSettingsPopoverOpen(false)}
                              className="px-4 py-2 hover:bg-[#4A4A56]"
                            >
                              {subItem.name}
                            </SidebarSubLink>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {subItem.name}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </PopoverContent>
                  </Popover>
                )
              ) : (
                <NavItemLink
                  to={item.route || '#'}
                  data-testid={'id-sidebar-list-item-button-' + item.name}
                  isActive={isActive}
                  centered={!open}
                  onClick={(event) => handleSidebarItemClick(event, index)}
                >
                  <span
                    className="shrink-0"
                    id={`id-sidebar-item-icon-${item.name}`}
                    data-testid={`sidebar-item-icon-${item.name}`}
                  >
                    {item.icon}
                  </span>
                  {open && (
                    <span
                      className="text-sm"
                      id={`id-sidebar-item-label-${item.name}`}
                      data-testid={`sidebar-item-label-${item.name}`}
                    >
                      {item.name}
                    </span>
                  )}
                </NavItemLink>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    );
  };

  return (
    <TooltipProvider>
      <>
        <div
          data-testid={'id-sidebar-container'}
          id={'id-sidebar-container'}
          className={cn(
            'shrink-0 transition-[width] duration-[225ms] ease-in-out',
            open ? 'w-56' : 'w-[50px]',
          )}
        >
          <aside
            className={cn(
              'fixed bottom-0 left-0 z-[1200] flex flex-col overflow-hidden bg-[#161616] text-white transition-[width] duration-[225ms] ease-in-out',
              open ? 'w-56' : 'w-[50px]',
            )}
            style={{ top: `${48 + topOffset}px` }}
          >
            {isTenantShow ? (
              <div
                className="box-border flex min-h-[56px] items-center border-b border-gray-700 px-2 py-3"
                onMouseEnter={handleTeamspaceMouseEnter}
                onMouseLeave={handleTeamspaceMouseLeave}
              >
                <DropdownMenu
                  modal={false}
                  open={teamspaceMenuOpen}
                  onOpenChange={(isOpen) => {
                    if (licenses && licenses.length === 0) return;
                    setTeamspaceMenuOpen(isOpen);
                  }}
                >
                  {open ? (
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="id-platform-teamspace-menu-button"
                        id="id-platform-teamspace-menu-button"
                        className={cn(
                          'flex w-full items-center gap-2 rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                          licenses && licenses.length === 0
                            ? 'pointer-events-none cursor-not-allowed'
                            : 'cursor-pointer',
                        )}
                      >
                        <TeamspaceAvatar
                          name={teamspaceName}
                          className="ml-1.5 shrink-0"
                          color={teamspaceColors[teamspaceId ?? '']}
                          fallbackProps={{
                            'data-testid':
                              'id-platform-teamspace-avatar-expanded',
                            id: 'id-platform-teamspace-avatar-expanded',
                          }}
                        />
                        <div className="ml-1 flex min-w-0 flex-1 flex-col">
                          <span
                            data-testid={'id-platform-selected-teamspace-name'}
                            id={'id-platform-selected-teamspace-name'}
                            className="truncate text-sm font-medium leading-tight text-white"
                          >
                            {teamspaceName}
                          </span>
                        </div>
                      </button>
                    </DropdownMenuTrigger>
                  ) : (
                    <Tooltip delayDuration={0}>
                      <DropdownMenuTrigger asChild>
                        <TooltipTrigger asChild>
                          <button
                            data-testid={'id-platform-teamspace-avatar'}
                            id={'id-platform-teamspace-avatar'}
                            className={cn(
                              'flex w-full items-center justify-center border-0 bg-transparent p-0',
                              licenses && licenses.length === 0
                                ? 'pointer-events-none cursor-not-allowed'
                                : 'cursor-pointer',
                            )}
                          >
                            <TeamspaceAvatar
                              name={teamspaceName}
                              className="mr-1"
                              color={teamspaceColors[teamspaceId ?? '']}
                            />
                          </button>
                        </TooltipTrigger>
                      </DropdownMenuTrigger>
                      <TooltipContent side="right">
                        {teamspaceName || t('create-teamspace')}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    sideOffset={8}
                    data-testid={'id-platform-teamspace-menu'}
                    id="id-platform-teamspace-menu"
                    className="!w-56 !rounded-lg !border-0 !bg-[#262626] !py-3 !text-[#F2F2F2]"
                    onMouseEnter={handleTeamspaceMouseEnter}
                    onMouseLeave={handleTeamspaceMouseLeave}
                  >
                    <DropdownMenuLabel
                      data-testid={'id-platform-switch-to-teamspace'}
                      id={'id-platform-switch-to-teamspace'}
                      className="!px-4 !pb-2 !text-sm !font-normal !text-gray-400"
                    >
                      {t('switch-to-teamspace')}
                    </DropdownMenuLabel>
                    <div
                      className="overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:hover:bg-gray-500 [&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar]:w-2"
                      style={{ maxHeight: 'min(500px, calc(100vh - 200px))' }}
                      data-testid="id-platform-teamspace-list-container"
                    >
                      {filteredTeamspaces.length > 1 ? (
                        filteredTeamspaces.map((option, index) =>
                          option?.id !== teamspaceId ? (
                            <DropdownMenuItem
                              data-testid={'id-platform-teamspace-menu-item'}
                              id={'id-platform-teamspace-menu-item'}
                              key={option?.id}
                              className="!gap-3 !px-4 !py-2 !text-sm !text-white data-[highlighted]:!bg-[#4A4A56]"
                              onSelect={() =>
                                handleTeamspaceMenuItemClick(index)
                              }
                            >
                              <TeamspaceAvatar
                                name={option?.name}
                                className="shrink-0"
                                color={teamspaceColors[option?.id ?? '']}
                                fallbackProps={{
                                  'data-testid':
                                    'id-platform-teamspace-warning-avatar',
                                  id: 'id-platform-teamspace-warning-avatar',
                                }}
                              />
                              {option?.name}
                              {option?.failedTask ? (
                                <span
                                  className="ml-2 inline-flex size-3 shrink-0 items-center justify-center rounded-full bg-destructive align-middle"
                                  id="id-sidebar-teamspace-warning-badge"
                                  data-testid="sidebar-teamspace-warning-badge"
                                >
                                  <AlertTriangle className="size-2 text-destructive-foreground" />
                                </span>
                              ) : null}
                            </DropdownMenuItem>
                          ) : null,
                        )
                      ) : (
                        <DropdownMenuItem
                          data-testid={'id-platform-no-other-teams'}
                          id={'id-platform-no-other-teams'}
                          className="!text-xs !italic !text-[#F2F2F2] !opacity-60"
                          disabled
                        >
                          {t('no-other-teams')}
                        </DropdownMenuItem>
                      )}
                    </div>
                    {currentTeamspaceRole === 'ROLE_ADMIN' ? (
                      <>
                        <DropdownMenuSeparator className="!bg-[#5C5C5C]" />
                        <DarkMenuItem
                          data-testid={'id-platform-edit-teamspace-menu-item'}
                          id={'id-platform-edit-teamspace-menu-item'}
                          onSelect={handleEditTeamspace}
                        >
                          <CarbonSettings size={16} className="mr-2" />
                          {t('edit-teamspace')}
                        </DarkMenuItem>
                      </>
                    ) : null}
                    {isAdmin ? (
                      <>
                        <DropdownMenuSeparator className="!bg-[#5C5C5C]" />
                        <DarkMenuItem
                          data-testid={'id-platform-create-teamspace-menu-item'}
                          id={'id-platform-create-teamspace-menu-item'}
                          onSelect={handleCreateTeamspace}
                        >
                          <Add size={16} className="mr-2" />
                          {t('create-teamspace')}
                        </DarkMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : null}

            {isLoopContextActive && (
              <LoopSwitcher
                expanded={open}
                activeLoopId={activeLoopId}
                activeLoopName={activeLoopName}
                completedLoops={completedLoops}
                teamspaceName={teamspaceName}
                onLoopSelect={(loopId, loopName) => {
                  setSelectedLoop({ id: loopId, name: loopName });
                  setLoopContext(loopId, loopName, teamspaceId || '');
                  navigate('/loop-details', {
                    state: { fromSwitcher: true },
                  });
                }}
                onAllLoopsClick={() => {
                  clearLoopContext();
                  navigate('/');
                }}
              />
            )}

            {buildSidebarItems()}

            <div
              className="fixed bottom-0 flex h-10 min-h-[40px] w-[inherit] items-center justify-center border-t border-gray-700 bg-[#161616]"
              id="id-sidebar-footer"
              data-testid="sidebar-footer"
            >
              <button
                data-testid={'id-platform-sidebar-arrow-icon'}
                id={'id-platform-sidebar-arrow-icon'}
                className="flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 text-gray-200 transition-colors hover:bg-[#1f2937]"
                onClick={open ? handleDrawerClose : handleDrawerOpen}
              >
                {open ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>
          </aside>
        </div>

        <EditModal
          isVisible={isEditModalVisible}
          onClose={handleCloseEditTeamspace}
          loopName={teamspaceName}
          loopId={teamspaceId}
          associatedUsers={associatedUsers}
          showSnackbar={showSnackbarForEditModal}
        />

        {snackbarState.open && (
          <div
            className="fixed right-4 top-4 z-[1300] flex min-w-[300px] max-w-sm items-start gap-3 rounded border border-border bg-white p-4 shadow-lg"
            style={{ borderLeft: getBorderColor(snackbarState.severity) }}
          >
            <div
              className="flex flex-1 items-center gap-2"
              id="id-sidebar-snackbar-content"
              data-testid="sidebar-snackbar-content"
            >
              {getAlertIcon(snackbarState.severity)}
              <span
                className="text-sm text-gray-900"
                id="id-sidebar-snackbar-message"
                data-testid="sidebar-snackbar-message"
              >
                {snackbarState.message}
              </span>
            </div>
            <button
              onClick={handleCloseSnackbar}
              className="shrink-0 text-gray-500 hover:text-gray-700"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </>
    </TooltipProvider>
  );
};

export default Sidebar;
